import { useState } from 'react';
import { Paperclip, Trash2, FileText, Upload, Sparkles } from 'lucide-react';
import { useSaveSection, useUploadDocument, useDeleteDocument } from './hooks.ts';
import { useAutosave } from './useAutosave.ts';
import { AutosaveStatus } from './AutosaveStatus.tsx';
import { apiErrorCode } from './api.ts';
import type { FieldDef, FieldValue, GroupBundle, SiteDocument } from './api.ts';

function fmtSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

const UPLOAD_ERRORS: Record<string, string> = {
  file_too_large: 'That PDF is too large.',
  invalid_pdf: "That file isn't a valid PDF.",
  invalid_upload: 'Upload failed — please choose a PDF.',
  missing_file: 'Please choose a file first.',
  invalid_body: 'Both Dhivehi and English titles are required.',
};

// ── Bilingual inputs (Dhivehi RTL/Thaana + English LTR) for one field ─────────

function BilingualInputs({
  field,
  value,
  onChange,
}: {
  field: FieldDef;
  value: FieldValue;
  onChange: (v: FieldValue) => void;
}): JSX.Element {
  const multiline = field.kind === 'richtext';
  const dvId = `${field.key}-dv`;
  const enId = `${field.key}-en`;
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1">
        <label htmlFor={dvId} className="status-label">Dhivehi</label>
        {multiline ? (
          <textarea
            id={dvId}
            dir="rtl"
            rows={4}
            className="textarea thaana"
            value={value.dv}
            onChange={(e) => onChange({ ...value, dv: e.target.value })}
          />
        ) : (
          <input
            id={dvId}
            dir="rtl"
            className="input thaana"
            value={value.dv}
            onChange={(e) => onChange({ ...value, dv: e.target.value })}
          />
        )}
      </div>
      <div className="space-y-1">
        <label htmlFor={enId} className="status-label">English</label>
        {multiline ? (
          <textarea
            id={enId}
            dir="ltr"
            rows={4}
            className="textarea"
            value={value.en}
            onChange={(e) => onChange({ ...value, en: e.target.value })}
          />
        ) : (
          <input
            id={enId}
            dir="ltr"
            className="input"
            value={value.en}
            onChange={(e) => onChange({ ...value, en: e.target.value })}
          />
        )}
      </div>
    </div>
  );
}

// ── Document attach control for docref fields ─────────────────────────────────

function DocRefControl({ group, field, documents }: { group: string; field: string; documents: SiteDocument[] }): JSX.Element {
  const upload = useUploadDocument();
  const del = useDeleteDocument();
  const [file, setFile] = useState<File | null>(null);
  const [titleDv, setTitleDv] = useState('');
  const [titleEn, setTitleEn] = useState('');

  const mine = documents.filter((d) => d.group === group && d.field === field);
  const canUpload = Boolean(file) && titleDv.trim() !== '' && titleEn.trim() !== '';

  function submit(): void {
    if (!file) return;
    upload.mutate(
      { file, titleDv: titleDv.trim(), titleEn: titleEn.trim(), group, field },
      { onSuccess: () => { setFile(null); setTitleDv(''); setTitleEn(''); } },
    );
  }

  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3 space-y-3">
      <p className="status-label flex items-center gap-1.5"><Paperclip size={12} /> Attached documents (PDF)</p>

      {mine.length > 0 ? (
        <ul className="space-y-1.5">
          {mine.map((d) => (
            <li key={d.id} className="flex items-center gap-2 rounded-md bg-paper border border-border px-2.5 py-1.5 text-sm">
              <FileText size={14} className="text-primary shrink-0" />
              <span className="min-w-0 flex-1 truncate">{d.titleEn}</span>
              <span className="mono-meta text-muted-foreground shrink-0">{fmtSize(d.sizeBytes)}</span>
              <button
                type="button"
                aria-label={`Delete ${d.titleEn}`}
                disabled={del.isPending}
                onClick={() => del.mutate(d.id)}
                className="text-muted-foreground hover:text-destructive disabled:opacity-50 shrink-0"
              >
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">No documents attached yet.</p>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <input
          dir="rtl"
          placeholder="ސުރުޚީ (Dhivehi title)"
          className="input thaana text-sm"
          value={titleDv}
          onChange={(e) => setTitleDv(e.target.value)}
        />
        <input
          dir="ltr"
          placeholder="English title"
          className="input text-sm"
          value={titleEn}
          onChange={(e) => setTitleEn(e.target.value)}
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="btn-secondary btn-sm cursor-pointer">
          <Upload size={13} /> {file ? 'Change PDF' : 'Choose PDF'}
          <input
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </label>
        {file && <span className="text-xs text-muted-foreground truncate max-w-[12rem]" dir="ltr">{file.name}</span>}
        <button type="button" className="btn-primary btn-sm" disabled={!canUpload || upload.isPending} onClick={submit}>
          {upload.isPending ? 'Uploading…' : 'Attach'}
        </button>
      </div>
      {upload.isError && (
        <p role="alert" className="text-xs text-destructive">
          {UPLOAD_ERRORS[apiErrorCode(upload.error) ?? ''] ?? 'Upload failed. Please try again.'}
        </p>
      )}
    </div>
  );
}

// ── One field block ───────────────────────────────────────────────────────────

function FieldBlock({
  field,
  group,
  value,
  documents,
  onChange,
}: {
  field: FieldDef;
  group: string;
  value: FieldValue;
  documents: SiteDocument[];
  onChange: (v: FieldValue) => void;
}): JSX.Element {
  return (
    <div className="card-paper p-4 space-y-3">
      <div className="space-y-0.5">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="font-medium text-foreground">{field.labelEn}</h3>
          {field.source && (
            <span className="pill pill-neutral" title="This content will auto-fill from a sibling app in a later release">
              <Sparkles size={11} /> auto-filled in a later release
            </span>
          )}
        </div>
        <p className="thaana text-sm text-muted-foreground" dir="rtl">{field.labelDv}</p>
      </div>
      <BilingualInputs field={field} value={value} onChange={onChange} />
      {field.kind === 'docref' && <DocRefControl group={group} field={field.key} documents={documents} />}
    </div>
  );
}

// ── Section form for one ICOM group ───────────────────────────────────────────
// Remount with a `key={group.group}` so switching tabs resets local state.

export function SectionPanel({ group, documents }: { group: GroupBundle; documents: SiteDocument[] }): JSX.Element {
  const save = useSaveSection();
  const [values, setValues] = useState<Record<string, FieldValue>>(() => {
    const init: Record<string, FieldValue> = {};
    for (const f of group.fields) init[f.key] = group.content[f.key] ?? { dv: '', en: '' };
    return init;
  });
  const autosave = useAutosave((fields: Record<string, FieldValue>) => save.mutateAsync({ group: group.group, fields }));

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="font-serif text-2xl tracking-tight text-ink">{group.titleEn}</h2>
          <p className="thaana-display text-lg text-muted-foreground" dir="rtl">{group.titleDv}</p>
        </div>
        <AutosaveStatus status={autosave.status} />
      </header>

      {group.fields.map((f) => (
        <FieldBlock
          key={f.key}
          field={f}
          group={group.group}
          documents={documents}
          value={values[f.key] ?? { dv: '', en: '' }}
          onChange={(v) =>
            setValues((prev) => {
              const next = { ...prev, [f.key]: v };
              autosave.schedule(next);
              return next;
            })
          }
        />
      ))}
    </div>
  );
}
